import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { GithubActionsApi } from '@backstage-community/plugin-github-actions';

type RequestOptions = {
  hostname?: string;
  owner: string;
  repo: string;
  path: string;
  query?: Record<string, string | number | undefined>;
  init?: RequestInit;
};

type GithubActionsApiResponse<TMethod extends keyof GithubActionsApi> =
  Awaited<ReturnType<GithubActionsApi[TMethod]>>;

export class GithubActionsBackendClient implements GithubActionsApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  private async request<T>({
    hostname,
    owner,
    repo,
    path,
    query,
    init,
  }: RequestOptions): Promise<T> {
    const baseUrl = await this.options.discoveryApi.getBaseUrl(
      'github-actions',
    );
    const url = new URL(`${baseUrl}${path}`);

    for (const [key, value] of Object.entries({
      hostname,
      owner,
      repo,
      ...query,
    })) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }

    const response = await this.options.fetchApi.fetch(url, init);
    if (!response.ok) {
      throw new Error(
        `GitHub Actions backend request failed: ${response.status} ${response.statusText}`,
      );
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      return (await response.json()) as T;
    }

    return (await response.text()) as T;
  }

  async listWorkflowRuns(options: {
    hostname?: string;
    owner: string;
    repo: string;
    pageSize?: number;
    page?: number;
    branch?: string;
  }): Promise<GithubActionsApiResponse<'listWorkflowRuns'>> {
    return this.request<GithubActionsApiResponse<'listWorkflowRuns'>>({
      ...options,
      path: '/actions/runs',
      query: {
        pageSize: options.pageSize,
        page: options.page,
        branch: options.branch,
      },
    });
  }

  async getWorkflow(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
  }): Promise<GithubActionsApiResponse<'getWorkflow'>> {
    return this.request<GithubActionsApiResponse<'getWorkflow'>>({
      ...options,
      path: `/actions/workflows/${options.id}`,
    });
  }

  async getWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
  }): Promise<GithubActionsApiResponse<'getWorkflowRun'>> {
    return this.request<GithubActionsApiResponse<'getWorkflowRun'>>({
      ...options,
      path: `/actions/runs/${options.id}`,
    });
  }

  async reRunWorkflow(options: {
    hostname?: string;
    owner: string;
    repo: string;
    runId: number;
  }): Promise<GithubActionsApiResponse<'reRunWorkflow'>> {
    return this.request<GithubActionsApiResponse<'reRunWorkflow'>>({
      ...options,
      path: `/actions/runs/${options.runId}/rerun`,
      init: { method: 'POST' },
    });
  }

  async listJobsForWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
    pageSize?: number;
    page?: number;
  }): Promise<GithubActionsApiResponse<'listJobsForWorkflowRun'>> {
    return this.request<GithubActionsApiResponse<'listJobsForWorkflowRun'>>({
      ...options,
      path: `/actions/runs/${options.id}/jobs`,
      query: {
        pageSize: options.pageSize,
        page: options.page,
      },
    });
  }

  async downloadJobLogsForWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    runId: number;
  }): Promise<GithubActionsApiResponse<'downloadJobLogsForWorkflowRun'>> {
    return this.request<
      GithubActionsApiResponse<'downloadJobLogsForWorkflowRun'>
    >({
      ...options,
      path: `/actions/jobs/${options.runId}/logs`,
    });
  }

  async listBranches(options: {
    hostname?: string;
    owner: string;
    repo: string;
    page: number;
  }): Promise<GithubActionsApiResponse<'listBranches'>> {
    return this.request<GithubActionsApiResponse<'listBranches'>>({
      ...options,
      path: '/repos/branches',
      query: { page: options.page },
    });
  }

  async getDefaultBranch(options: {
    hostname?: string;
    owner: string;
    repo: string;
  }): Promise<GithubActionsApiResponse<'getDefaultBranch'>> {
    return this.request<GithubActionsApiResponse<'getDefaultBranch'>>({
      ...options,
      path: '/repos/default-branch',
    });
  }
}
